import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import {css, StyleDeclaration, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import HelpButton from 'help/HelpButton';
import {FONT_PROXIMA_11} from 'styles/FontConstants';
import {GRAY_DARKER, RED_DARK} from 'ui-library/Colors';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';

import FieldLabel from './FieldLabel';
import {InputErrorMessageStyle, InputProps} from './InputConstants';

export interface InputWrapperProps extends InputProps {
  children: React.ReactNode;
  dataTestAttrs?: DataTestAttrs;
  id: string;
  helpInteraction?: FloatingInteraction.CLICK | FloatingInteraction.HOVER;
}

const styles = StyleSheet.create({
  errorMessage: {
    ...FONT_PROXIMA_11,
  },
  errorMessageInfo: {
    color: GRAY_DARKER,
  },
  errorMessageError: {
    color: RED_DARK,
  },
});

const ERROR_MESSAGE_STYLE: Record<InputErrorMessageStyle, StyleDeclaration> = {
  [InputErrorMessageStyle.INFO]: styles.errorMessageInfo,
  [InputErrorMessageStyle.ERROR]: styles.errorMessageError,
};

/**
 * Component for input of string fields, providing labels, icons, and validation.
 * Label styles are re-exported on this class.
 */
export default function InputWrapper({
  className,
  dataTestAttrs,
  labelStyle = FieldLabel.STYLES.HIDDEN,
  id,
  isRequired = false,
  label,
  help,
  helpInteraction = FloatingInteraction.CLICK,
  description,
  descriptionClassName,
  children,
  button,
  error,
}: InputWrapperProps): JSX.Element {
  return (
    <div className={className} {...getTestProps(dataTestAttrs)}>
      <div
        className={classNames('fieldInput-label-container', {
          'sr-only': labelStyle === FieldLabel.STYLES.HIDDEN,
        })}
      >
        <FieldLabel htmlFor={id} isRequired={isRequired} label={label} labelStyle={labelStyle} />
        {help && (
          <HelpButton
            renderContent={() => help}
            interaction={helpInteraction}
            iconClassName="u-mini-spacing-left"
          />
        )}
      </div>
      {description && (
        <div className={classNames('fieldInput-description', descriptionClassName)}>{description}</div>
      )}
      <div className="u-flex-display">
        {children}
        {button && React.cloneElement(button, {className: 'button--inline'})}
      </div>
      {error && (
        <div
          role="status"
          className={classNames(css(styles.errorMessage), css(ERROR_MESSAGE_STYLE[error.style]))}
        >
          {error.message}
        </div>
      )}
    </div>
  );
}
